import React from 'react';
import { PLACEHOLDER_ARTICULO } from './utils/placeholders';

const ContentSection = ({ title, items, showViewAll = true, onViewAll, onSelectItem, onAudioClick }) => {

  // Función para obtener imagen con fallbacks
  const getImageUrl = (item) => {
    return item.image || 
           item.imageUrl || 
           item.thumbnail || 
           PLACEHOLDER_ARTICULO;
  };

  // Función para obtener el icono según la sección
  const getSectionIcon = (sectionTitle) => {
    const icons = {
      'Evangelio': '📖',
      'Buenos días': '🌅', 
      'Clave agustiniana': '🔑',
      'Santos Agustinos': '👼',
      'Vocaciones': '✨',
      'Vamos al cole': '🎒',
      'Te interesa': '🌍',
      'Está pasando': '📰',
      'Eventos': '🎪'
    };
    return icons[sectionTitle] || '📄';
  };

  // Función para obtener el color de fondo del icono según la sección
  const getIconGradient = (sectionTitle, index = 0) => {
    const gradients = {
      'Evangelio': 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)',
      'Buenos días': 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', 
      'En clave agustiniana': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'Santos Agustinos': 'linear-gradient(135deg, #ffd700 0%, #ffb347 100%)',
      'Vocaciones': 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
      'Vamos al cole': 'linear-gradient(135deg, #48cae4 0%, #023e8a 100%)',
      'Te interesa': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'Está pasando': 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      'Eventos': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    };
    return gradients[sectionTitle] || 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)';
  };

  return (
    <section className="mb-4 md:mb-6 lg:mb-8 px-1 md:px-6 lg:px-8 no-overflow">
      {/* Encabezado de sección */}
      <div className="flex justify-between items-center mb-4 px-4 md:px-0">
        <h2 className="responsive-title text-primary flex items-center gap-2">
          <span className="text-xl md:text-2xl lg:text-3xl">{getSectionIcon(title)}</span>
          {title}
        </h2>
        {showViewAll && (
          <button 
            onClick={() => {
              console.log(`🔍 ContentSection - "Ver todos" clickeado para: ${title}`);
              onViewAll && onViewAll(title);
            }}
            className="text-sm font-semibold hover:underline transition-colors"
            style={{ color: 'var(--primary-red)' }}
          >
            Ver todos
          </button>
        )}
      </div>

      {/* Lista de items */}
      {title === 'Evangelio' ? (
        /* Carrusel horizontal para Evangelio */
        <div className="relative w-full overflow-hidden">
          <div 
            className="flex gap-3 md:gap-4 overflow-x-auto custom-scrollbar pb-4"
            style={{
              scrollSnapType: 'x mandatory',
              scrollBehavior: 'smooth'
            }}
          >
            {items && items.length > 0 ? (
              items.map((item, index) => (
                <div 
                  key={item.id || index}
                  className="modern-card stagger-animation animate-slide-up cursor-pointer flex-shrink-0"
                  onClick={() => {
                    console.log(`🎯 ContentSection - Artículo clickeado: ${item.title}`);
                    onSelectItem && onSelectItem(item, title);
                  }}
                  style={{
                    minWidth: '260px',
                    maxWidth: '280px',
                    width: '70vw',
                    scrollSnapAlign: 'start'
                  }}
                >
                  {/* Imagen destacada del evangelio */}
                  {item.originalData?.imageUrl ? (
                    <div className="w-full h-32 rounded-xl overflow-hidden mb-4 bg-gradient-to-br from-red-50 to-red-100">
                      <img 
                        src={getImageUrl(item)}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.log(`❌ Error cargando imagen para: ${item.title}`);
                          e.target.parentNode.innerHTML = `<div class="w-full h-32 rounded-xl flex items-center justify-center text-4xl" style="background: ${getIconGradient(title, index)}">${getSectionIcon(title)}</div>`;
                        }}
                      />
                    </div>
                  ) : (
                    <div 
                      className="w-full h-32 rounded-xl flex items-center justify-center text-4xl mb-4"
                      style={{
                        background: getIconGradient(title, index)
                      }}
                    >
                      {getSectionIcon(title)}
                    </div>
                  )}

                  {/* Contenido del evangelio */}
                  <div>
                    <h3 className="text-lg font-bold text-primary mb-3 line-clamp-2 leading-tight">
                      {item.title}
                    </h3>
                    
                    {item.description && (
                      <p className="text-secondary text-sm line-clamp-3 leading-relaxed mb-3 whitespace-pre-wrap">
                        {item.description}
                      </p>
                    )}

                    {/* Metadatos del evangelio */}
                    <div className="flex items-center justify-between text-xs text-secondary">
                      <div className="flex items-center gap-2">
                        {item.originalData?.date && (
                          <span className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded-full">
                            📅 {new Date(item.originalData.date).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </span>
                        )}
                        {item.originalData?.hasAudio && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevenir que se abra el artículo
                              console.log(`🎵 Audio clickeado para: ${item.title}`);
                              onAudioClick && onAudioClick(item.originalData, title);
                            }}
                            className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full text-green-600 font-medium hover:bg-green-100 transition-colors cursor-pointer"
                          >
                            🎵 Audio
                          </button>
                        )}
                      </div>
                      
                      {item.originalData?.author && item.originalData.author !== 'Agustinos' && (
                        <span className="flex items-center gap-1 text-xs opacity-75">
                          ✏️ {item.originalData.author}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="modern-card text-center py-12 flex-shrink-0" style={{ 
                minWidth: '260px',
                maxWidth: '280px',
                width: '70vw'
              }}>
                <div className="section-icon mx-auto mb-4" style={{
                  background: getIconGradient(title)
                }}>
                  {getSectionIcon(title)}
                </div>
                <h3 className="text-lg font-semibold text-primary mb-2">
                  No hay contenido disponible
                </h3>
                <p className="text-secondary text-sm">
                  Esta sección se actualizará pronto con nuevo contenido.
                </p>
              </div>
            )}
          </div>
          
          {/* INDICADORES DE PUNTOS ELIMINADOS */}
        </div>
      ) : (
        /* Layout vertical para otras secciones */
        <div className="space-y-4">
          {items && items.length > 0 ? (
            items.slice(0, 1).map((item, index) => (
              <div 
                key={item.id || index}
                className="modern-card stagger-animation animate-slide-up cursor-pointer"
                onClick={() => {
                  console.log(`🎯 ContentSection - Artículo clickeado: ${item.title}`);
                  onSelectItem && onSelectItem(item, title);
                }}
              >
                <div className="flex items-start gap-5">
                  {/* Thumbnail o icono de la sección */}
                  {item.originalData?.imageUrl ? (
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                      <img 
                        src={getImageUrl(item)}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.log(`❌ Error cargando imagen para: ${item.title}`);
                          // Si falla la imagen, mostrar el icono por defecto
                          e.target.parentNode.innerHTML = `<div class="section-icon" style="background: ${getIconGradient(title, index)}; width: 80px; height: 80px; border-radius: 12px;">${getSectionIcon(title)}</div>`;
                        }}
                      />
                    </div>
                  ) : (
                    <div 
                      className="section-icon flex-shrink-0"
                      style={{
                        background: getIconGradient(title, index),
                        width: '80px',
                        height: '80px',
                        borderRadius: '12px'
                      }}
                    >
                      {getSectionIcon(title)}
                    </div>
                  )}

                  {/* Contenido del artículo */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-primary mb-2 line-clamp-2 leading-tight">
                      {item.title}
                    </h3>
                    
                    {item.description && (
                      <p className="text-secondary text-sm line-clamp-2 leading-relaxed mb-2 whitespace-pre-wrap">
                        {item.description}
                      </p>
                    )}

                    {/* Metadatos adicionales */}
                    <div className="flex items-center gap-3 text-xs text-secondary">
                      {/* Fecha si está disponible */}
                      {item.originalData?.date && (
                        <span className="flex items-center gap-1">
                          📅 {new Date(item.originalData.date).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short'
                          })}
                        </span>
                      )}

                      {/* Indicador de audio */}
                      {item.originalData?.hasAudio && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevenir que se abra el artículo
                            console.log(`🎵 Audio clickeado para: ${item.title}`);
                            onAudioClick && onAudioClick(item.originalData, title);
                          }}
                          className="flex items-center gap-1 text-green-600 font-medium hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded-full transition-colors cursor-pointer"
                        >
                          🎵 Audio
                        </button>
                      )}

                      {/* Autor si está disponible */}
                      {item.originalData?.author && item.originalData.author !== 'Agustinos' && (
                        <span className="flex items-center gap-1">
                          ✏️ {item.originalData.author}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Estado vacío con diseño mejorado
            <div className="modern-card text-center py-12">
              <div className="section-icon mx-auto mb-4" style={{
                background: getIconGradient(title),
                width: '80px',
                height: '80px',
                borderRadius: '12px'
              }}>
                {getSectionIcon(title)}
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">
                No hay contenido disponible
              </h3>
              <p className="text-secondary text-sm">
                Esta sección se actualizará pronto con nuevo contenido.
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ContentSection;